import { getCroppedImg } from '@/common/canvasUtils';
import { Button } from '@/design-system/button';
import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent } from 'ui/dialog';

type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Props = {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onCropDone: (file: File, previewUrl: string) => void;
  type?: 'host' | 'store';
  cropShape?: 'rect' | 'round';
};

const dialogContentStyles = cva('p-0 gap-0 rounded bg-card flex flex-col max-h-[90vh] border-none', {
  variants: {
    size: {
      crop: 'sm:max-w-[580px] w-[90vw]',
    },
  },
  defaultVariants: {
    size: 'crop',
  },
});

const topBarStyles = cva('px-6 py-4 border-b-[2px] border-b-border-divider flex items-center justify-between gap-3');

const bodyStyles = cva('px-6 pt-4 pb-8 overflow-y-auto flex-1');

export const ImageCropModal = ({ open, imageSrc, onClose, onCropDone, type = 'host', cropShape = 'rect' }: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const { t } = useDynamicLocale();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropComplete = useCallback((_: unknown, croppedAreaPixelsValue: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleDone = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      setIsCropping(true);
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      const croppedFile = new File([croppedBlob], `cropped-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      const previewUrl = URL.createObjectURL(croppedBlob);
      onCropDone(croppedFile, previewUrl);
      // ❌ Remove: onClose() — parent closes via onCropDone handler
    } catch {
      // Keep the modal open so the user can retry the crop.
    } finally {
      setIsCropping(false);
    }
  };

  if (!imageSrc) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isCropping) {
          onClose();
        }
      }}
    >
      <DialogContent className={cn(dialogContentStyles())}>
        <div className={topBarStyles()}>
          <div className="text-base font-medium text-foreground">{t('common.page.modal.cropImage.title')}</div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isCropping}>
              {t('common.page.button.cancel')}
            </Button>

            <Button
              type="button"
              variant="default"
              onClick={() => {
                void handleDone();
              }}
              isLoading={isCropping}
              disabled={isCropping}
            >
              {t('common.page.button.apply')}
            </Button>
          </div>
        </div>

        <div className={bodyStyles()}>
          <div className="flex flex-col gap-4">
            <div className="relative h-[400px] w-full overflow-hidden rounded-md bg-overlay">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={cropShape == 'round' ? 1 / 1 : type === 'host' ? 3 / 4 : 16 / 9}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                cropShape={cropShape}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="crop-zoom">
                {t('common.page.modal.zoom')}
              </label>
              <input
                id="crop-zoom"
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
